/**
 * Utility functions for formatting P&G (Profit & Loss) data
 */

/**
 * Format a number as currency with thousands separator
 * @param value Number to format
 * @param includeSymbol Whether to include the € symbol
 * @returns Formatted string (e.g., "3.271.485 €" or "3.271.485")
 */
export function formatCurrency(value: number, includeSymbol: boolean = true): string {
  if (value === null || value === undefined || isNaN(value)) {
    return includeSymbol ? '0 €' : '0';
  }

  // Use absolute value for formatting
  const absValue = Math.abs(value);

  // Format with Spanish locale (thousands separator: ., decimal separator: ,)
  const formatter = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const formatted = formatter.format(absValue);

  return includeSymbol ? `${formatted} €` : formatted;
}

/**
 * Format a number as percentage with 1 decimal place
 * @param value Number to format (e.g., 100.0, -65.0)
 * @returns Formatted string (e.g., "100,0%", "-65,0%")
 */
export function formatPercentage(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0,0%';
  }

  // Format with 1 decimal place, preserving the sign
  const formatter = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });

  return `${formatter.format(value)}%`;
}

/**
 * Format a negative number with parentheses
 * @param value Number to format
 * @param includeSymbol Whether to include the € symbol
 * @returns Formatted string with parentheses if negative (e.g., "(2.125.000)")
 */
export function formatNegative(value: number, includeSymbol: boolean = false): string {
  if (value === null || value === undefined || isNaN(value)) {
    return includeSymbol ? '0 €' : '0';
  }

  const formatted = formatCurrency(Math.abs(value), includeSymbol);

  return value < 0 ? `(${formatted})` : formatted;
}

/**
 * Format variation percentage with sign (+ or -)
 * @param value Variation value (e.g., -6.7, 298.7)
 * @returns Formatted string with sign (e.g., "-6,7%", "+298,7%")
 */
export function formatVariation(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0,0%';
  }

  const formatter = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'always'
  });

  return `${formatter.format(value)}%`;
}

/**
 * Export table data to CSV format
 * @param data Array of objects to export
 * @param filename Name of the CSV file
 * @param headers Optional custom headers
 */
export function exportToCSV(
  data: any[],
  filename: string = 'cuenta_resultados.csv',
  headers?: string[]
): void {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from first object if not provided
  const csvHeaders = headers || Object.keys(data[0]);

  // Create CSV content
  let csvContent = csvHeaders.join(';') + '\n';

  data.forEach(row => {
    const values = csvHeaders.map(header => {
      const value = row[header];

      // Handle null/undefined
      if (value === null || value === undefined) {
        return '';
      }

      // Handle strings with quotes
      if (typeof value === 'string') {
        return `"${value.replace(/"/g, '""')}"`;
      }

      return value;
    });

    csvContent += values.join(';') + '\n';
  });

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Calculate percentage over sales
 * @param value Value to calculate percentage for
 * @param totalSales Total sales (base 100%)
 * @returns Percentage value
 */
export function calculatePercentageOverSales(value: number, totalSales: number): number {
  if (!totalSales || totalSales === 0) {
    return 0;
  }

  return (Math.abs(value) / totalSales) * 100;
}
